// TelcoTrack — Úřady: ručně přidané řádky vs. import z mapy (DTM/WFS).

import {
  AUTHORITY_CONSTRUCTION_PREP_KEYS,
  AUTHORITY_CONSTRUCTION_PREP_LABELS_CS,
  catalogKeyForPrep,
  isAuthorityPovinnostKey,
  isCoreAuthorityPovinnost,
  type AuthorityAgendaKind,
  type AuthorityConstructionPrepKey,
} from './authority-domain.constants';
import { parseAuthorityOverlayMap } from './authority-overlay-map.constants';
import {
  AUTHORITY_DISCOVERY_CATALOG,
  catalogKeyForNetwork,
  catalogKeyForNetworkStakeout,
  documentTypeForSubject,
  isAuthorityVytyceniObligation,
  isNetworkStakeoutMatrixKey,
  NETWORK_STAKEOUT_DOCUMENT_TYPE,
  NETWORK_STAKEOUT_LABEL_CS,
  networkCatalogOwnerKey,
  networkSubjectFromStakeoutMatrixKey,
} from './authority-urady-categories.constants';

/** `evidence_payload.origin` written by the stavba + form (not overlay apply). */
export const AUTHORITY_MANUAL_OBLIGATION_ORIGIN = 'manual';

export type ManualObligationTypeGroupId = 'obeznameni' | 'vytyceni' | 'povinnosti';

export interface ManualObligationTypeOption {
  value: string;
  label: string;
}

export interface ManualObligationTypeGroup {
  id: ManualObligationTypeGroupId;
  labelCs: string;
  options: ManualObligationTypeOption[];
}

export interface ManualObligationCatalogPick {
  documentType: string;
  subjectKey: string;
  agendaKind: AuthorityAgendaKind;
  catalogKey: string | null;
  labelCs: string;
}

export type ManualObligationOriginInput = {
  ruleSource?: string | null;
  evidencePayload?: Record<string, unknown> | null;
  subjectKey?: string | null;
  documentType?: string | null;
  catalogKey?: string | null;
};

function payloadRecord(
  payload: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  return payload;
}

function evidenceOrigin(payload: Record<string, unknown> | null | undefined): string {
  const rec = payloadRecord(payload);
  return String(rec?.['origin'] ?? '').trim();
}

/** Import sítí z mapy / DTM / WFS overlay — not a stavba + row. */
export function isAuthorityMapImportedObligation(input: ManualObligationOriginInput | null | undefined): boolean {
  if (!input) return false;
  if ((input.ruleSource ?? '').trim() === 'overlay') return true;
  const payload = payloadRecord(input.evidencePayload);
  if (!payload) return false;
  if (parseAuthorityOverlayMap(payload)) return true;
  const dtm = payload['dtmIdentity'];
  if (dtm && typeof dtm === 'object' && !Array.isArray(dtm)) return true;
  const contact = payload['overlayContact'];
  if (contact && typeof contact === 'object' && !Array.isArray(contact)) {
    return String((contact as Record<string, unknown>)['source'] ?? '').trim() === 'overlay';
  }
  return false;
}

/** Created via + on stavba cards (form sets origin + `ruleSource: human`). */
export function isAuthorityManualObligation(input: ManualObligationOriginInput | null | undefined): boolean {
  if (!input || isAuthorityMapImportedObligation(input)) return false;
  if (evidenceOrigin(input.evidencePayload) === AUTHORITY_MANUAL_OBLIGATION_ORIGIN) return true;
  return (input.ruleSource ?? '').trim() === 'human';
}

/**
 * Cross-category document type is only for human + rows.
 * Overlay imports, catalog-seeded core povinnosti, and auto vytyčení siblings stay locked.
 */
export function canChangeAuthorityObligationDocumentType(
  input: ManualObligationOriginInput | null | undefined,
): boolean {
  if (!input) return true;
  if (isAuthorityMapImportedObligation(input)) return false;
  const rule = (input.ruleSource ?? '').trim();
  if (rule === 'system' || rule === 'overlay') return false;
  if (isCoreAuthorityPovinnost(input) && rule !== 'human') return false;
  return isAuthorityManualObligation(input) || rule === 'legacy';
}

export function manualObligationEvidencePayload(
  extra: Record<string, unknown> = {},
): Record<string, unknown> {
  return { ...extra, origin: AUTHORITY_MANUAL_OBLIGATION_ORIGIN };
}

/**
 * Hub lock for Nové obeznámení / vytyčení / povinnosti — keep the type picker
 * inside the agenda the user opened from (not the full 3-group catalog).
 */
export function resolveManualObligationTypeGroupId(input: {
  documentType?: string | null;
  subjectKey?: string | null;
  agendaKind?: string | null;
}): ManualObligationTypeGroupId | null {
  const documentType = (input.documentType ?? '').trim();
  const subjectKey = (input.subjectKey ?? '').trim();
  const agenda = (input.agendaKind ?? '').trim();
  const catalogValue = documentType || subjectKey;
  if (catalogValue) {
    if (isAuthorityVytyceniObligation({ documentType: catalogValue, subjectKey: catalogValue })) {
      return 'vytyceni';
    }
    if (isAuthorityPovinnostKey(catalogValue) && catalogValue !== 'stakeout') {
      return 'povinnosti';
    }
    const discovered = AUTHORITY_DISCOVERY_CATALOG.find((row) => row.subjectKey === catalogValue);
    if (discovered?.category === 'krizeni_siti') return 'obeznameni';
  }
  if (agenda === 'construction_prep') return 'povinnosti';
  if (agenda === 'network_crossing') return 'obeznameni';
  return null;
}

/** Same catalog as Nová povinnost / + on Obeznámení and Vytyčení. */
export function buildManualObligationTypeGroups(
  extraValues: readonly string[] = [],
  onlyGroupId?: ManualObligationTypeGroupId | null,
): ManualObligationTypeGroup[] {
  const obeznameni: ManualObligationTypeOption[] = AUTHORITY_DISCOVERY_CATALOG.filter(
    (row) => row.category === 'krizeni_siti',
  ).map((row) => ({ value: row.subjectKey, label: row.labelCs }));
  const vytyceni: ManualObligationTypeOption[] = [
    { value: NETWORK_STAKEOUT_DOCUMENT_TYPE, label: NETWORK_STAKEOUT_LABEL_CS },
  ];
  const povinnosti: ManualObligationTypeOption[] = AUTHORITY_CONSTRUCTION_PREP_KEYS.filter(
    (key) => key !== 'stakeout',
  ).map((key) => ({
    value: key,
    label: AUTHORITY_CONSTRUCTION_PREP_LABELS_CS[key],
  }));
  const groups: ManualObligationTypeGroup[] = [
    { id: 'obeznameni', labelCs: 'Obeznámení', options: obeznameni },
    { id: 'vytyceni', labelCs: 'Vytyčení', options: vytyceni },
    { id: 'povinnosti', labelCs: 'Povinnosti', options: povinnosti },
  ];
  const known = new Set(groups.flatMap((group) => group.options.map((opt) => opt.value)));
  for (const raw of extraValues) {
    const value = raw.trim();
    if (!value || known.has(value)) continue;
    const groupId = groupIdForCatalogValue(value);
    if (onlyGroupId && groupId !== onlyGroupId) continue;
    const group = groups.find((row) => row.id === groupId);
    if (!group) continue;
    group.options.push({ value, label: extraTypeLabel(value) });
    known.add(value);
  }
  if (onlyGroupId) {
    return groups.filter((group) => group.id === onlyGroupId);
  }
  return groups;
}

export function resolveManualObligationCatalogPick(
  value: string,
  existing?: {
    id?: string | null;
    catalogKey?: string | null;
    subjectKey?: string | null;
    municipality?: string | null;
    documentType?: string | null;
  } | null,
): ManualObligationCatalogPick | null {
  const key = value.trim();
  if (!key) return null;
  if (isAuthorityPovinnostKey(key) && key !== 'stakeout') {
    const prep = key as AuthorityConstructionPrepKey;
    return {
      documentType: prep,
      subjectKey: prep,
      agendaKind: 'construction_prep',
      catalogKey: catalogKeyForPrep(prep),
      labelCs: AUTHORITY_CONSTRUCTION_PREP_LABELS_CS[prep],
    };
  }
  if (key === NETWORK_STAKEOUT_DOCUMENT_TYPE || isNetworkStakeoutMatrixKey(key) || key === 'stakeout') {
    const subject =
      networkSubjectFromStakeoutMatrixKey(key) ||
      (existing?.subjectKey ?? '').trim() ||
      'custom_sit';
    const owner =
      networkCatalogOwnerKey(existing?.catalogKey, subject) || (existing?.id ?? '').trim() || null;
    return {
      documentType: NETWORK_STAKEOUT_DOCUMENT_TYPE,
      subjectKey: subject,
      agendaKind: 'network_crossing',
      catalogKey: catalogKeyForNetworkStakeout(subject, owner),
      labelCs: NETWORK_STAKEOUT_LABEL_CS,
    };
  }
  const discovered = AUTHORITY_DISCOVERY_CATALOG.find((row) => row.subjectKey === key);
  if (discovered?.category === 'krizeni_siti') {
    const documentType = documentTypeForSubject(key);
    return {
      documentType,
      subjectKey: key,
      agendaKind: 'network_crossing',
      catalogKey: catalogKeyForNetwork(key, existing?.municipality),
      labelCs: discovered.labelCs,
    };
  }
  return null;
}

function groupIdForCatalogValue(value: string): ManualObligationTypeGroupId {
  if (isAuthorityVytyceniObligation({ documentType: value, subjectKey: value })) return 'vytyceni';
  if (isAuthorityPovinnostKey(value)) return 'povinnosti';
  return 'obeznameni';
}

function extraTypeLabel(value: string): string {
  const discovered = AUTHORITY_DISCOVERY_CATALOG.find((row) => row.subjectKey === value);
  if (discovered) return discovered.labelCs;
  if (isAuthorityPovinnostKey(value)) {
    return AUTHORITY_CONSTRUCTION_PREP_LABELS_CS[value as AuthorityConstructionPrepKey];
  }
  if (value === NETWORK_STAKEOUT_DOCUMENT_TYPE) return NETWORK_STAKEOUT_LABEL_CS;
  return value;
}
